import { HandCoins, RotateCcw, ArrowLeftRight, FileEdit } from 'lucide-react';
import { ResolutionType, assertNever, electionDesignationLabel } from '@signalvote/shared';
import type { ContributionDto } from '@signalvote/shared';
import { useResolutionChain } from '@/hooks/queries/useFinanceContributions';
import { cn } from '@/lib/utils';
import { formatAmount, formatDate } from '../utils';

interface ResolutionChainViewProps {
  contributionId: string | null;
}

function getResolutionIcon(resolutionType: ResolutionType | null) {
  if (resolutionType === null) return <HandCoins className="h-4 w-4" />;
  switch (resolutionType) {
    case ResolutionType.Refund:
      return <RotateCcw className="h-4 w-4" />;
    case ResolutionType.Reattribution:
      return <ArrowLeftRight className="h-4 w-4" />;
    case ResolutionType.WrittenRedesignation:
    case ResolutionType.PresumptiveRedesignation:
      return <FileEdit className="h-4 w-4" />;
    default:
      return assertNever(resolutionType);
  }
}

function getResolutionLabel(item: ContributionDto): string {
  if (!item.resolutionType) return 'Original';

  switch (item.resolutionType) {
    case ResolutionType.Refund:
      return 'Refund';
    case ResolutionType.Reattribution:
      return `Reattribution → ${item.contactName}`;
    case ResolutionType.WrittenRedesignation:
      return 'Written Redesignation';
    case ResolutionType.PresumptiveRedesignation:
      return 'Presumptive Redesignation';
    default:
      return assertNever(item.resolutionType);
  }
}

// A gift with no cures has a one-item chain, which says nothing the row doesn't, so the history
// renders only once something has been done to the gift.
export function ResolutionChainView({ contributionId }: ResolutionChainViewProps) {
  const { data } = useResolutionChain(contributionId);
  const chain = data?.resolutionChain ?? [];

  if (!contributionId || chain.length < 2) return null;

  return (
    <section aria-labelledby={`resolution-history-${contributionId}`} className="space-y-1">
      <h3 id={`resolution-history-${contributionId}`} className="text-sm font-medium">
        Resolution history
      </h3>
      <div className="space-y-0">
        {chain.map((item, index) => (
          <div
            key={item.id}
            data-testid="resolution-chain-item"
            className={cn(
              'flex items-start gap-3 border-l-2 py-2 pl-4',
              index === 0 ? 'border-primary' : 'border-muted',
            )}
          >
            <div className="mt-0.5 shrink-0 text-muted-foreground">
              {getResolutionIcon(item.resolutionType ?? null)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <span className="text-sm font-medium">{getResolutionLabel(item)}</span>
                <span className={cn(
                  'text-sm font-semibold',
                  item.amount < 0 ? 'text-red-600' : 'text-foreground',
                )}>
                  {formatAmount(item.amount)}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{electionDesignationLabel(item.electionDesignation, item.electionYear)}</span>
                <span>&middot;</span>
                <span>{formatDate(item.date)}</span>
                {item.contactName && item.resolutionType === ResolutionType.Reattribution && (
                  <>
                    <span>&middot;</span>
                    <span>{item.contactName}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
